import type { FormProps } from '../../types'
import cx from 'classnames'
import { CheckCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useController } from 'react-hook-form'
import style from '../form.module.css'

export type RadioOption = {
  label: string
  value: string
}

export interface CustomRadioGroupInput {
  name: string
  type: string
  customControlType: 'radioGroup'
  value?: string | number
  options: RadioOption[]
  validation?: {
    required?: boolean
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
    step?: number
  }
  uiOptions?: {
    testId?: string
  }
}

export type CustomRadioGroupProps = {
  field: CustomRadioGroupInput
  formProps: FormProps
}

export default function CustomRadioGroup({
  field,
  formProps,
}: CustomRadioGroupProps) {
  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  const [selectedValues, setSelectedValues] = useState<string[]>(value || [])

  // Update local state when value changes from external source
  useEffect(() => {
    // Ensure value is always an array
    setSelectedValues(Array.isArray(value) ? value : value ? [value] : [])
  }, [value])

  // Handle selection toggle
  const handleSelect = (optionValue: string) => {
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue) // Remove if already selected
      : [...selectedValues, optionValue] // Add if not selected

    setSelectedValues(newSelectedValues)
    onChange(newSelectedValues)
  }

  const options = field?.options ?? []

  return (
    <div className="grid gap-2 pt-4">
      {options?.map((option) => {
        const isSelected = selectedValues.includes(option.value)
        return (
          <div
            key={`${field.name}-${option.value}`}
            className={cx(
              style.radioGroup,
              'cursor-pointer bg-background-light p-3 rounded-md flex items-center text-slate-50',
              isSelected ? 'border border-primary bg-primary text-primary-foreground' : 'border border-base-input',

            )}
            data-testid={`${field.uiOptions?.testId ?? field.name}-${option.value
              }`}
            onClick={() => handleSelect(option.value)}
          >
            <div className="flex items-center gap-2">
              {isSelected && (
                <CheckCircle2
                  className="h-6 w-6 fill-primary"
                  stroke="white"
                  strokeWidth={2}
                />
              )}
              <input
                type="checkbox"
                value={option.value}
                checked={isSelected}
                className="sr-only border border-base-input"
                id={`${field.name}-${option.value}`}
                data-testid={`${field.uiOptions?.testId ?? field.name}-${option.value
                  }-input`}
                onChange={(e) => {
                  // This prevents the onChange from firing twice with the onClick
                  e.stopPropagation()
                  handleSelect(option.value)
                }}
              />
              <span className="font-normal">{option.label}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
